import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Aperture, Clapperboard, Film, Gem, Plus } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const SERVICES = [
  {
    id: "weddings",
    icon: Gem,
    title: "Destination Weddings",
    desc: "Two-day cinematic coverage of your celebration, anywhere on the map.",
    items: ["4K / 8K master film", "60-sec teaser in 72 hrs", "300+ graded photographs", "Drone coverage included"],
  },
  {
    id: "commercial",
    icon: Clapperboard,
    title: "Commercial Cinema",
    desc: "Brand films and product stories with a theatrical finish.",
    items: ["Concept to colour grade", "Anamorphic look", "Licensed score & sound design", "Deliverables for all platforms"],
  },
  {
    id: "portraits",
    icon: Aperture,
    title: "Editorial Portraits",
    desc: "Studio and environmental portraits with magazine-grade retouching.",
    items: ["Half-day studio session", "20 fine-retouched frames", "Print-ready masters", "Wardrobe & mood consult"],
  },
  {
    id: "documentary",
    icon: Film,
    title: "Fine-Art Documentaries",
    desc: "Long-form storytelling for families, foundations and festivals.",
    items: ["Multi-day production", "Interview direction", "Archival restoration", "Festival-cut delivery"],
  },
];

export const Services = () => {
  const [open, setOpen] = useState(null);

  return (
    <section id="services" data-testid="services-section" className="relative py-28 sm:py-36 bg-coal/40 border-y border-ivory/5">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-10">
        <div className="mb-14">
          <SectionHeading eyebrow="Commissions" title="Services & Craft" testid="services-heading" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const isOpen = open === s.id;
            return (
              <Reveal key={s.id} delay={i * 0.1}>
                <button
                  data-testid={`services-card-${s.id}`}
                  onClick={() => setOpen(isOpen ? null : s.id)}
                  className={`w-full h-full text-left border p-7 transition-all duration-500 group ${
                    isOpen
                      ? "border-amber/50 bg-raise shadow-[0_10px_40px_rgba(0,0,0,0.6)]"
                      : "border-ivory/10 bg-coal hover:border-ivory/30 hover:-translate-y-1"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <Icon size={22} className={`transition-colors duration-500 ${isOpen ? "text-amber" : "text-ash group-hover:text-amber"}`} />
                    <Plus
                      size={16}
                      className={`text-mute transition-transform duration-500 ${isOpen ? "rotate-45 text-amber" : ""}`}
                    />
                  </div>
                  <h3 className="font-display text-lg text-ivory tracking-wide mt-8">{s.title}</h3>
                  <p className="font-mono text-[11px] leading-relaxed text-ash mt-3">{s.desc}</p>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.ul
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pt-5 mt-5 border-t border-line space-y-2.5">
                          {s.items.map((item) => (
                            <li key={item} className="font-mono text-[10px] tracking-[0.15em] uppercase text-cream flex items-center gap-2.5">
                              <span className="w-1 h-1 rotate-45 bg-amber" />
                              {item}
                            </li>
                          ))}
                        </div>
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
